import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Menu,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as ActivateIcon,
  Done as CheckIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Role } from '../../types/admin.types';
import { enqueueSnackbar } from 'notistack';
import { adminService } from '../../services/admin.service';
import { SystemUser, Tenant, UserCreateRequest } from '../../types/admin.types';
import { PaginatedResponse } from '../../types/api.types';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

// ── Permission preview for a given role ───────────────────────────────────────
const PERM_GROUPS: { label: string; perms: string[] }[] = [
  { label: 'Workflows', perms: ['workflow.view', 'workflow.create', 'workflow.edit', 'workflow.delete', 'workflow.execute'] },
  { label: 'Executions', perms: ['execution.view', 'execution.cancel'] },
  { label: 'Credentials', perms: ['credential.view', 'credential.create', 'credential.edit', 'credential.delete'] },
  { label: 'Admin', perms: ['user.manage', 'org.manage'] },
];

interface RolePermPreviewProps {
  roleName: string;
  roles: Role[];
}

const RolePermPreview: React.FC<RolePermPreviewProps> = ({ roleName, roles }) => {
  const role = roles.find((r) => r.name === roleName);
  if (!role) return null;
  const perms: string[] = role.permissions || [];
  const hasAll = perms.includes('*');

  return (
    <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Permissions granted by <strong>{roleName}</strong> role
      </Typography>
      {hasAll ? (
        <Typography variant="body2" sx={{ mt: 0.5, color: 'primary.main', fontWeight: 600 }}>
          ★ All permissions (full access)
        </Typography>
      ) : (
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableBody>
            {PERM_GROUPS.map((group) => (
              <TableRow key={group.label} sx={{ '&:last-child td': { border: 0 } }}>
                <TableCell sx={{ py: 0.5, pl: 0, pr: 1, width: 90, color: 'text.secondary', fontSize: 11, fontWeight: 600, border: 0 }}>
                  {group.label}
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 0, border: 0 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {group.perms.map((p) => {
                      const has = perms.includes(p);
                      const label = p.split('.')[1];
                      return (
                        <Tooltip key={p} title={p}>
                          <Chip
                            size="small"
                            label={label}
                            icon={has ? <CheckIcon /> : <CancelIcon />}
                            color={has ? 'success' : 'default'}
                            variant={has ? 'filled' : 'outlined'}
                            sx={{ fontSize: 10, opacity: has ? 1 : 0.45, height: 20 }}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export const UserManagement: React.FC = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<SystemUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [showPermPreview, setShowPermPreview] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);

  const [newUser, setNewUser] = useState<UserCreateRequest>({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    organization_id: '',
    role: 'editor',
  });

  const hasPerm = (perm: string) => {
    if (!user) return false;
    if (user.role === 'system_admin') return true;
    return Array.isArray((user as any).permissions) && (user as any).permissions.includes(perm);
  };

  const canManageUsers = hasPerm('user.manage');

  useEffect(() => {
    loadTenants();
    adminService.getRoles().then((r) => setRoles(Array.isArray(r) ? r : [])).catch(() => {});
  }, []);

  // Debounce search: wait 400ms after user stops typing before triggering load
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch]);

  const loadTenants = async () => {
    try {
      const resp = await adminService.getTenants({ page: 1, page_size: 200, ordering: 'name' });
      setTenants(resp.results || []);
    } catch (err: any) {
      // Non-blocking; user can still type org id manually.
      enqueueSnackbar(err?.response?.data?.error || 'Failed to load tenants', { variant: 'warning' });
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const resp: PaginatedResponse<SystemUser> = await adminService.getUsers({
        page: page + 1, // DRF is 1-based
        page_size: pageSize,
        search: debouncedSearch.trim() || undefined,
        ordering: 'email',
      });
      setUsers(resp.results || []);
      setRowCount(resp.count || 0);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to load users', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!canManageUsers) {
      enqueueSnackbar('User management permission required.', { variant: 'error' });
      return;
    }
    try {
      await adminService.createUser(newUser);
      setCreateDialogOpen(false);
      setNewUser({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        organization_id: '',
        role: 'editor',
      });
      enqueueSnackbar('User created', { variant: 'success' });
      setPage(0);
      loadUsers();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to create user', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (user: SystemUser) => {
    if (!canManageUsers) {
      enqueueSnackbar('User management permission required.', { variant: 'error' });
      return;
    }
    try {
      await adminService.updateUser(user.id, { is_active: !user.is_active });
      enqueueSnackbar(user.is_active ? 'User deactivated' : 'User activated', { variant: 'success' });
      loadUsers();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to update user', { variant: 'error' });
    }
  };

  const handleDeleteUser = async (user: SystemUser) => {
    if (!canManageUsers) {
      enqueueSnackbar('User management permission required.', { variant: 'error' });
      return;
    }
    try {
      await adminService.deleteUser(user.id);
      enqueueSnackbar('User deleted', { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to delete user', { variant: 'error' });
    }
  };

  const handleChangeRole = async (user: SystemUser, role: SystemUser['role']) => {
    if (!canManageUsers) {
      enqueueSnackbar('User management permission required.', { variant: 'error' });
      return;
    }
    try {
      await adminService.updateUser(user.id, { role });
      enqueueSnackbar('Role updated', { variant: 'success' });
      setRoleDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.error || 'Failed to update role', { variant: 'error' });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: SystemUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      case 'editor':
        return 'primary';
      case 'viewer':
        return 'default';
      case 'system_admin':
        return 'info';
      default:
        return 'default';
    }
  };

  const columns: GridColDef[] = [
    { field: 'first_name', headerName: 'First Name', flex: 1, minWidth: 120 },
    { field: 'last_name', headerName: 'Last Name', flex: 1, minWidth: 120 },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 200 },
    {
      field: 'organization',
      headerName: 'Organization',
      flex: 1,
      minWidth: 170,
      valueGetter: (params) => (params.row?.organization?.name ? params.row.organization.name : ''),
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          color={getRoleColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    { field: 'last_login', headerName: 'Last Login', width: 180 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row as SystemUser)}
          size="small"
          disabled={!canManageUsers}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Typography variant="h4">User Management</Typography>
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 240 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={!canManageUsers}
          >
            Create User
          </Button>
        </Box>
      </Box>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={users}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          checkboxSelection
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              autoFocus
              margin="dense"
              label="First Name"
              fullWidth
              variant="outlined"
              value={newUser.first_name}
              onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Last Name"
              fullWidth
              variant="outlined"
              value={newUser.last_name}
              onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
            />
          </Box>
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            variant="outlined"
            type="email"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Password"
            fullWidth
            variant="outlined"
            type="password"
            value={newUser.password}
            onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value as any })}
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="editor">Editor</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
            </Select>
          </FormControl>

          {/* Permission preview for selected role */}
          <Box sx={{ mt: 0.5, mb: 1 }}>
            <Button
              size="small"
              variant="text"
              startIcon={showPermPreview ? <CollapseIcon /> : <ExpandIcon />}
              onClick={() => setShowPermPreview((v) => !v)}
              sx={{ fontSize: 12, color: 'text.secondary', pl: 0 }}
            >
              {showPermPreview ? 'Hide' : 'Show'} permissions for this role
            </Button>
            <Collapse in={showPermPreview}>
              <RolePermPreview roleName={newUser.role || 'editor'} roles={roles} />
            </Collapse>
          </Box>

          <FormControl fullWidth margin="dense">
            <InputLabel>Organization</InputLabel>
            <Select
              value={newUser.organization_id}
              label="Organization"
              onChange={(e) => setNewUser({ ...newUser, organization_id: String(e.target.value || '') })}
            >
              <MenuItem value="">
                <em>Select an organization</em>
              </MenuItem>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            Tip: if you leave password empty, the user will be created without a usable password and must reset it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={!canManageUsers}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog
        open={roleDialogOpen}
        onClose={() => {
          setRoleDialogOpen(false);
          setSelectedUser(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Role — {selectedUser?.email}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedUser?.role || 'editor'}
              label="Role"
              onChange={(e) => {
                const role = e.target.value as SystemUser['role'];
                if (selectedUser) setSelectedUser({ ...selectedUser, role });
              }}
            >
              <MenuItem value="viewer">Viewer</MenuItem>
              <MenuItem value="editor">Editor</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="owner">Owner</MenuItem>
              <MenuItem value="system_admin">System Admin</MenuItem>
            </Select>
          </FormControl>
          {/* Permission preview for selected role */}
          <RolePermPreview roleName={selectedUser?.role || 'editor'} roles={roles} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => selectedUser && handleChangeRole(selectedUser, selectedUser.role)}
            disabled={!canManageUsers}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedUser(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete{' '}
            <b>
              {selectedUser
                ? `${selectedUser.first_name} ${selectedUser.last_name}`.trim() || selectedUser.email
                : 'this user'}
            </b>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => selectedUser && handleDeleteUser(selectedUser)}
            disabled={!canManageUsers}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedUser) handleToggleStatus(selectedUser);
            handleMenuClose();
          }}
          disabled={!canManageUsers}
        >
          {selectedUser?.is_active ? <BlockIcon /> : <ActivateIcon />}
          {selectedUser?.is_active ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setRoleDialogOpen(true);
            handleMenuClose();
          }}
          disabled={!canManageUsers}
        >
          <EditIcon />
          Change role
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          disabled={!canManageUsers}
        >
          <DeleteIcon />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};